/**
 * External Imports
 */
import { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';

/**
 * Internal Imports
 */
import SearchField from '../../../../searchHeader/searchField/SearchField';
import FeatureNotEnabled from '../../../../../pages/errorPage/FeatureNotEnabled';
import { Dropdown } from '../../../../shared/dropdowns';
import Table from '../../../../shared/table';

import { RootState } from '../../../../../redux/store';
import { useGetSassTokenQuery } from '../../../../../redux/apis/Sass.api';
import { usePostUpdateCurrentFacilityMutation } from '../../../../../redux/apis/Users.api';

import { useUserRolesRef } from '../../../../../services/UserRoles';
import { useDebounceValue } from '../../../../../utils/debouncers';
import {
  formatRoundingType,
  handleBlobPicUrl,
  toTitleCaseSpecial,
  userProfilePicUrl
} from '../../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  CNEdEmployee,
  CSCEmployee,
  ValidationEmployee,
  IFacility
} from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';
import {
  ContentHeader,
  EmployeeAvatar,
  EmployeeNameInTable,
  EmployeeSlot,
  FacilityInput,
  HideButton,
  ManagerNameInTable,
  Meta,
  MyEmployeesContainer,
  RemoveButton,
  ShowButton,
  RoundingConfigContainer,
  RoundingConfigHeader,
  TextInTable
} from './SupportListSettingsTable.styles';

/**
 * Static File Imports
 */
import defaultImage from '../../../../../assets/images/defaultImage.png';

type SupportEmployee = CSCEmployee | CNEdEmployee | ValidationEmployee;

interface SupportListSettingsProps {
  authorizedUser?: AuthUserProps;
  employees: SupportEmployee[];
  facilities: IFacility[];
  facilityId: string;
  isFacilitySelectorEnabled: boolean;
  isTableEnabled: boolean;
  loadingSupportList: boolean;
  type: string;
  userId: string;
  hideEmployee: (employee: SupportEmployee) => void;
}

const SupportListSettingsTable: FC<SupportListSettingsProps> = ({
  authorizedUser,
  employees = [],
  facilities,
  facilityId,
  isFacilitySelectorEnabled,
  isTableEnabled,
  loadingSupportList,
  type,
  userId,
  hideEmployee
}) => {
  const [selectedFacility, setSelectedFacility] = useState(facilityId);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [sortKey, setSortKey] = useState<string>('lastName');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('asc');
  const [active, setActive] = useState<string>('Employee');

  const [isFacilityOpen, setIsFacilityOpen] = useState(false);
  const [activeSearch, setActiveSearch] = useState<string>('');
  const [query, setQuery] = useState('');

  const debouncedQuery = useDebounceValue(query, 250);
  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { isEnterpriseAdmin } = useUserRolesRef();

  const [
    updateCurrentFacility
    // {
    //   isError: isUpdateCurrentFacilityError,
    //   isLoading: isUpdateCurrentFacilityLoading,
    //   isSuccess: isUpdateCurrentFacilitySuccess
    // }
  ] = usePostUpdateCurrentFacilityMutation();
  const {
    data: sasTokenData,
    // isError: isGetSassTokenError,
    isFetching: isGetSassTokenFetching
    // isLoading: isGetSassTokenLoading
  } = useGetSassTokenQuery();

  const handleHideEmployee = (employee: SupportEmployee) => {
    hideEmployee(employee);
    setSearchTerm('');
  };

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Employee':
        key = 'lastName';
        break;
      case 'Department':
        key = 'dept';
        break;
      case 'Title':
        key = 'title';
        break;
      case 'Manager':
        key = 'managerFullName';
        break;
      case 'Show on List':
        key = 'visible';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  // TODO: Comment out until 'Add Employees' functionality is in scope
  // const openEditModal = () => {
  //   dispatch(setModalType('Add Employees'));
  //   dispatch(openModal());
  // };

  const handleTypeAheadChange = useCallback(
    (e: any) => setQuery(e.target.value),
    []
  );

  const setFacilityForUpdate = async (facility: IFacility) => {
    await updateCurrentFacility({
      userId,
      facilityId: facility.facilityId
    });
    setSelectedFacility(facility.facilityId);
    setActiveSearch('');
    setQuery('');
  };

  const filteredList = useMemo(() => {
    const formattedSearchTerm = dbSearchTerm.toLowerCase();
    const filtered = employees.filter(
      (employee: SupportEmployee) =>
        employee.employeeName.toLowerCase().includes(formattedSearchTerm) ||
        employee.hcaid.includes(formattedSearchTerm)
    );

    return filtered;
  }, [dbSearchTerm, employees]);

  const facilitySearchResults = useMemo(() => {
    switch (activeSearch) {
      case 'facility':
        return facilities.filter((facility: IFacility) =>
          facility.value
            .toLowerCase()
            .includes(debouncedQuery.trim().toLowerCase())
        );
      default:
        return undefined;
    }
  }, [activeSearch, facilities, debouncedQuery]);

  const selectedFacilityName = useMemo(
    () =>
      facilities?.find(
        (item: IFacility) => item.facilityId === selectedFacility
      )?.value,
    [facilities, selectedFacility]
  );

  const sortedList = useMemo(() => {
    const stateCopy = [...filteredList];

    if (sortKey === 'visible') {
      stateCopy.sort((a: SupportEmployee, b: SupportEmployee) =>
        a[sortKey as keyof SupportEmployee] >
        b[sortKey as keyof SupportEmployee]
          ? sortDir === 'asc'
            ? -1
            : 1
          : sortDir === 'asc'
          ? 1
          : -1
      );
    } else {
      stateCopy.sort((a: SupportEmployee, b: SupportEmployee) =>
        a[sortKey as keyof SupportEmployee]?.toLowerCase() >
        b[sortKey as keyof SupportEmployee]?.toLowerCase()
          ? sortDir === 'asc'
            ? 1
            : -1
          : sortDir === 'asc'
          ? -1
          : 1
      );
    }

    return stateCopy;
  }, [filteredList, sortKey, sortDir]);

  const isTableEmpty = useMemo(
    () => !dbSearchTerm && sortedList.length === 0,
    [dbSearchTerm, sortedList]
  );

  const isTableEmptySearch = useMemo(
    () => dbSearchTerm && sortedList.length === 0,
    [dbSearchTerm, sortedList]
  );

  const headers: JSX.Element[] = [
    <CellContainer
      key="Employee"
      id={`${type}-Employee-List-Settings-Table-Employee-Column`}
      unit="column"
      colWidth={20}
      direction={!(active === 'Employee') ? 'none' : sortDir}
      sort
    >
      Employee
    </CellContainer>,
    <CellContainer
      key="Department"
      id={`${type}-Employee-List-Settings-Table-Dept-Column`}
      unit="column"
      colWidth={24}
      direction={!(active === 'Department') ? 'none' : sortDir}
      sort
    >
      Department
    </CellContainer>,
    <CellContainer
      key="Title"
      id={`${type}-Employee-List-Settings-Table-Title-Column`}
      unit="column"
      colWidth={22}
      direction={!(active === 'Title') ? 'none' : sortDir}
      sort
    >
      Title
    </CellContainer>,
    <CellContainer
      key="Manager"
      id={`${type}-Employee-List-Settings-Table-Manager-Column`}
      unit="column"
      colWidth={20}
      direction={!(active === 'Manager') ? 'none' : sortDir}
      sort
    >
      Manager
    </CellContainer>,
    <CellContainer
      key="Show On List"
      id={`${type}-Employee-List-Settings-Table-Hide-Column`}
      unit="column"
      colWidth={14}
      direction={!(active === 'Show on List') ? 'none' : sortDir}
      sort
    >
      Show on List
    </CellContainer>
  ];

  const rows = sortedList.map((employee: SupportEmployee, i: number) => {
    return (
      <StyledNeuTableRow
        // eslint-disable-next-line react/no-array-index-key
        key={`Support-Employee-Row-${i}`}
        id={`${type}-Employee-Row-${employee.hcaid}`}
        unit="row"
        size="default"
      >
        <CellContainer unit="column" colWidth={20}>
          <EmployeeSlot>
            {sasTokenData && sasTokenData.sasToken !== '' ? (
              <EmployeeAvatar
                src={
                  handleBlobPicUrl(
                    employee.pictureUrl,
                    sasTokenData.sasToken
                  ) ?? userProfilePicUrl(employee.hcaid, sasTokenData.sasToken)
                }
                alt="Employee Image"
                onError={e => {
                  e.currentTarget.src = defaultImage;
                  return null;
                }}
              />
            ) : (
              <EmployeeAvatar src={defaultImage} alt="Default Image" />
            )}
            <div>
              <EmployeeNameInTable className="my-0">
                {employee.employeeName}
              </EmployeeNameInTable>
              <TextInTable className="my-0">{employee.hcaid}</TextInTable>
            </div>
          </EmployeeSlot>
        </CellContainer>
        <CellContainer unit="column" colWidth={24}>
          <Meta style={{ whiteSpace: 'normal' }}>
            <TextInTable>{employee.dept}</TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={22}>
          <Meta>
            <TextInTable style={{ whiteSpace: 'normal' }}>
              {employee.title}
            </TextInTable>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={20}>
          <Meta>
            <div>
              <ManagerNameInTable className="my-0">
                {employee.managerFullName || 'Unknown'}
              </ManagerNameInTable>
              <TextInTable className="my-0">
                {employee.managerHcaid}
              </TextInTable>
            </div>
          </Meta>
        </CellContainer>
        <CellContainer unit="column" colWidth={14}>
          <RemoveButton onClick={() => handleHideEmployee(employee)}>
            {employee.visible ? (
              <HideButton color="orange" kind="secondary" size="small">
                Hide
              </HideButton>
            ) : (
              <ShowButton color="blue" kind="secondary" size="small">
                Show
              </ShowButton>
            )}
          </RemoveButton>
        </CellContainer>
      </StyledNeuTableRow>
    );
  });

  return (
    <MyEmployeesContainer id={`${type}-Employee-List-Settings-Container`}>
      <RoundingConfigContainer>
        <ContentHeader>
          <SearchField searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </ContentHeader>
        {authorizedUser && isFacilitySelectorEnabled && (
          <FacilityInput>
            <RoundingConfigHeader className="mb-0 pe-3">
              {formatRoundingType(type)} Facility
            </RoundingConfigHeader>
            <div className="position-relative">
              <Dropdown
                classes="ps-0"
                disabled={
                  !isEnterpriseAdmin &&
                  authorizedUser.access?.accessLevel === 'facility' &&
                  authorizedUser.access?.accessLevelIds.length <= 1
                }
                disabledWithTransparent={
                  !isEnterpriseAdmin &&
                  authorizedUser.access?.accessLevel === 'facility' &&
                  authorizedUser.access?.accessLevelIds.length <= 1
                }
                focused={activeSearch === 'facility'}
                name="facility"
                open={isFacilityOpen}
                placeholder={selectedFacilityName || 'Select a Facility'}
                query={query}
                typeAhead
                styles={{ paddingTop: '3px' }}
                setOpen={setIsFacilityOpen}
                setTypeAhead={setActiveSearch}
                setTypeAheadChange={handleTypeAheadChange}
              >
                {activeSearch === 'facility' &&
                facilitySearchResults &&
                facilitySearchResults.length > 0 ? (
                  facilitySearchResults.map((facility: IFacility) => (
                    <div
                      key={facility.id}
                      className={`dropdown-hover-item${
                        facility.facilityId === facilityId ? ' selected' : ''
                      }`}
                      aria-selected={facility.facilityId === facilityId}
                      role="option"
                      tabIndex={0}
                      style={{ paddingLeft: '0.75em' }}
                      onClick={() => setFacilityForUpdate(facility)}
                      onKeyDown={() => setFacilityForUpdate(facility)}
                    >
                      {toTitleCaseSpecial(facility.value)}
                    </div>
                  ))
                ) : (
                  <div className="dropdown-search-no-result">
                    No results found!
                  </div>
                )}
              </Dropdown>
            </div>
          </FacilityInput>
        )}
      </RoundingConfigContainer>
      {isTableEnabled ? (
        <Table
          customClass={`${type}-accounts-settings`}
          headers={headers}
          rows={rows}
          isTableEmpty={isTableEmpty}
          isTableEmptySearch={isTableEmptySearch}
          loading={loadingSupportList || isGetSassTokenFetching}
          type="supportList"
          onSort={(e: any) => handleSort(e.detail.label)}
        />
      ) : (
        <FeatureNotEnabled offsetHeight={263} />
      )}
    </MyEmployeesContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    facilities: state.ConfigReducer.userAccessibleFacilities,
    facilityId: state.AuthorizedUser.authorizedUser.facilityId,
    userId: state.AuthorizedUser.authorizedUser.hcaid
  };
};
export default connect(mapReduxStateToProps)(SupportListSettingsTable);
